import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Upload, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BulkUpload = () => {
  const [uploadState, setUploadState] = useState({
    isUploading: false,
    uploadComplete: false,
    uploadResponse: null,
    error: null
  });

  const downloadTemplate = async () => {
    try {
      console.log('🔄 Starting template download...');
      
      // Create template data
      const templateData = [
        ['client_name', 'organization_name', 'gstin', 'pan', 'email', 'mobile', 'whatsapp', 'gstin_password', 'ewaybill_username', 'ewaybill_password', 'billing_start_date', 'gst_enabled', 'state'],
        ['ABC Enterprises', 'ABC Pvt Ltd', '27ABCDE1234F1Z5', 'ABCDE1234F', 'contact@abc.com', '+919876543210', '+919876543210', 'password123', 'abc_user', 'eway_pass', '2024-01-01', 'true', 'Maharashtra'],
        ['XYZ Corporation', 'XYZ Corp', '', 'XYZDE5678G', 'info@xyz.com', '+918765432109', '', '', '', '', '2024-01-15', 'false', 'Karnataka']
      ];

      // Convert to CSV format for download
      const csvContent = templateData.map(row => 
        row.map(cell => `"${cell}"`).join(',')
      ).join('\n');

      console.log('📄 CSV content created');

      // Try multiple download methods for better browser compatibility
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Method 1: Traditional download
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        // For IE/Edge
        window.navigator.msSaveOrOpenBlob(blob, 'client_bulk_upload_template.csv');
        console.log('✅ Downloaded via IE/Edge method');
        return;
      }
      
      // Method 2: Modern browsers
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'client_bulk_upload_template.csv'; // Use download attribute
      link.style.display = 'none';
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
      
      console.log('✅ Template download initiated');
      
    } catch (error) {
      console.error('❌ Client-side download failed, trying backend method:', error);
      
      // Fallback: Backend download
      try {
        console.log('🔄 Attempting backend download...');
        window.open(`${API}/clients/bulk-upload/template`, '_blank');
        console.log('✅ Backend download initiated');
      } catch (backendError) {
        console.error('❌ Backend download also failed:', backendError);
        alert(`Download failed: ${error.message}. Please try the alternative method or contact support.`);
      }
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.csv')) {
      alert('Please select an Excel (.xlsx) or CSV file');
      return;
    }

    setUploadState({
      isUploading: true,
      uploadComplete: false,
      uploadResponse: null,
      error: null
    });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/clients/bulk-upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadState({
        isUploading: false,
        uploadComplete: true,
        uploadResponse: response.data,
        error: null
      });

    } catch (error) {
      setUploadState({
        isUploading: false,
        uploadComplete: false,
        uploadResponse: null,
        error: error.response?.data?.detail || 'Upload failed'
      });
    }
  };

  const resetUpload = () => {
    setUploadState({
      isUploading: false,
      uploadComplete: false,
      uploadResponse: null,
      error: null
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50">
      {/* Header Section */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-orange-100/50 px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
                Bulk Client Upload
              </h1>
              <p className="text-gray-600 text-lg">Upload multiple clients at once using Excel or CSV templates</p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center space-x-4">
              <Button 
                onClick={downloadTemplate}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg"
              >
                <Upload className="w-5 h-5 mr-2" />
                Download Template
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-4xl mx-auto space-y-8">
        {/* Template Information */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
            <CardTitle className="text-2xl font-bold flex items-center">
              <Upload className="w-6 h-6 mr-3" />
              Upload Instructions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Template Fields:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-medium text-red-600">Required Fields:</h4>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 mt-2">
                    <li>client_name</li>
                    <li>organization_name</li>
                    <li>email</li>
                    <li>mobile</li>
                    <li>state</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-blue-600">Optional Fields:</h4>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 mt-2">
                    <li>gstin</li>
                    <li>pan</li>
                    <li>whatsapp</li>
                    <li>gstin_password</li>
                    <li>ewaybill_username</li>
                    <li>ewaybill_password</li>
                    <li>billing_start_date</li>
                    <li>gst_enabled (true/false)</li>
                  </ul>
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" />
                  <div>
                    <h4 className="font-medium text-yellow-800">Important Notes:</h4>
                    <ul className="text-yellow-700 text-sm mt-1 space-y-1">
                      <li>• Download the template first to ensure correct format</li>
                      <li>• Required fields cannot be empty</li>
                      <li>• Email format will be validated</li>
                      <li>• Mobile numbers should include country code</li>
                      <li>• Date format: YYYY-MM-DD</li>
                      <li>• GST enabled should be 'true' or 'false'</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* File Upload Area */}
        {!uploadState.uploadComplete && (
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
            <CardContent className="p-8">
              <div className="text-center">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-blue-400 transition-colors">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {uploadState.isUploading ? 'Processing...' : 'Upload Client Data'}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {uploadState.isUploading 
                      ? 'Please wait while we process your file...' 
                      : 'Select an Excel (.xlsx) or CSV file to upload'
                    }
                  </p>
                  {uploadState.isUploading ? (
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept=".xlsx,.csv"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="file-upload"
                      />
                      <label 
                        htmlFor="file-upload"
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 cursor-pointer"
                      >
                        Choose File
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upload Results */}
        {uploadState.uploadComplete && uploadState.uploadResponse && (
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
            <CardHeader className={`${uploadState.uploadResponse.success ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-yellow-500 to-orange-600'} text-white`}>
              <CardTitle className="text-2xl font-bold flex items-center">
                {uploadState.uploadResponse.success ? <CheckCircle className="w-6 h-6 mr-3" /> : <AlertCircle className="w-6 h-6 mr-3" />}
                Upload {uploadState.uploadResponse.success ? 'Successful' : 'Completed with Issues'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{uploadState.uploadResponse.total_processed}</div>
                  <div className="text-blue-800">Total Processed</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{uploadState.uploadResponse.successful}</div>
                  <div className="text-green-800">Successfully Added</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{uploadState.uploadResponse.failed}</div>
                  <div className="text-red-800">Failed</div>
                </div>
              </div>
              
              {uploadState.uploadResponse.errors && uploadState.uploadResponse.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-red-800 mb-2">Errors:</h4>
                  <ul className="text-red-700 text-sm space-y-1">
                    {uploadState.uploadResponse.errors.map((error, index) => (
                      <li key={index}>• Row {error.row}: {error.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-center">
                <Button onClick={resetUpload} className="bg-gradient-to-r from-blue-500 to-indigo-600">
                  Upload Another File
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error Display */}
        {uploadState.error && (
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-red-500 to-pink-600 text-white">
              <CardTitle className="text-2xl font-bold flex items-center">
                <XCircle className="w-6 h-6 mr-3" />
                Upload Failed
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-red-600 mb-4">{uploadState.error}</p>
              <Button onClick={resetUpload} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BulkUpload;
